import { apiService } from './apiService';
import { API_ENDPOINTS, buildApiUrl, getEndpoint } from './apiEndpoints';

export const accountService = {
  login: async (credentials) => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.LOGIN, credentials);
  },

  logout: async () => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.LOGOUT);
  },

  signup: async (userData) => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.SIGNUP, userData);
  },

  getProfile: async () => {
    return await apiService.get(API_ENDPOINTS.ACCOUNTS.PROFILE);
  },

  updateProfile: async (profileData) => {
    return await apiService.put(API_ENDPOINTS.ACCOUNTS.PROFILE, profileData);
  },

  patchProfile: async (profileData) => {
    return await apiService.patch(API_ENDPOINTS.ACCOUNTS.PROFILE, profileData);
  },

  deleteProfile: async () => {
    return await apiService.delete(API_ENDPOINTS.ACCOUNTS.PROFILE);
  },

  changePin: async (pinData) => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.CHANGE_PIN, pinData);
  },

  forgotPin: async (forgotData) => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.FORGOT_PIN, forgotData);
  },

  requestOtp: async (requestData) => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.REQUEST_OTP, requestData);
  },

  verifyOtp: async (verifyData) => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.VERIFY_OTP, verifyData);
  },

  refreshToken: async (refreshData) => {
    return await apiService.post(API_ENDPOINTS.ACCOUNTS.TOKEN_REFRESH, refreshData);
  },

  testProtected: async () => {
    return await apiService.get(API_ENDPOINTS.ACCOUNTS.PROTECTED);
  },

  getStates: async () => {
    return await apiService.get(API_ENDPOINTS.ACCOUNTS.STATES);
  },

  getDistricts: async (stateId) => {
    if (!stateId) {
      return [];
    }
    return await apiService.get(getEndpoint(API_ENDPOINTS.ACCOUNTS.DISTRICTS, stateId));
  },
};

export const buyerService = {
  signup: async (buyerData) => {
    return await apiService.post(API_ENDPOINTS.BUYERS.SIGNUP, buyerData);
  },

  getProfile: async () => {
    return await apiService.get(API_ENDPOINTS.BUYERS.PROFILE);
  },

  getProfileById: async (buyerId) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.BUYERS.PROFILE_BY_ID, buyerId));
  },

  updateProfile: async (buyerId, profileData) => {
    const endpoint = getEndpoint(API_ENDPOINTS.BUYERS.PROFILE_UPDATE, buyerId);
    return await apiService.patch(endpoint, profileData);
  },

  deleteProfile: async () => {
    return await apiService.delete(API_ENDPOINTS.BUYERS.PROFILE_DELETE);
  },

  changeProfileImage: async (imageData) => {
    return await apiService.patch(API_ENDPOINTS.BUYERS.PROFILE_CHANGE_IMAGE, imageData);
  },

 getInquiryList: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.BUYERS.INQUIRY_LIST, { params });
  },

  submitInquiry: async (inquiryData) => {
    return await apiService.post(API_ENDPOINTS.BUYERS.INQUIRY_SUBMIT, inquiryData);
  },

  updateInquiry: async (inquiryId, inquiryData) => {
    return await apiService.patch(getEndpoint(API_ENDPOINTS.BUYERS.INQUIRY_UPDATE, inquiryId), inquiryData);
  },

  getInterests: async () => {
    return await apiService.get(API_ENDPOINTS.BUYERS.INTERESTS_GET);
  },

  getInterestOptions: async () => {
    return await apiService.get(API_ENDPOINTS.BUYERS.INTERESTS_OPTIONS);
  },

  submitInterests: async (interestsData) => {
    return await apiService.post(API_ENDPOINTS.BUYERS.INTERESTS_SUBMIT, interestsData);
  },

  clickSanitarywareInquiry: async (productId, clickData) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.BUYERS.SANITARYWARE_INQUIRY_CLICK, productId), clickData);
  },

  clickTileInquiry: async (productId, clickData) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.BUYERS.TILE_INQUIRY_CLICK, productId), clickData);
  },

  getSanitarywareInquiryClicks: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.BUYERS.SANITARYWARE_INQUIRY_CLICK_LIST, { params });
  },

  getTileInquiryClicks: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.BUYERS.TILE_INQUIRY_CLICK_LIST, { params });
  },
};

export const sellerService = {
  signup: async (sellerData) => {
    return await apiService.post(API_ENDPOINTS.SELLERS.SIGNUP, sellerData);
  },

  getProfile: async () => {
    return await apiService.get(API_ENDPOINTS.SELLERS.PROFILE);
  },

  getProfileById: async (sellerId) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.SELLERS.PROFILE_BY_ID, sellerId));
  },

  updateProfile: async (sellerId, profileData) => {
    const endpoint = getEndpoint(API_ENDPOINTS.SELLERS.PROFILE_UPDATE, sellerId);
    return await apiService.patch(endpoint, profileData);
  },

  deleteProfile: async () => {
    return await apiService.delete(API_ENDPOINTS.SELLERS.PROFILE_DELETE);
  },

  changeProfileImage: async (imageData) => {
    return await apiService.patch(API_ENDPOINTS.SELLERS.PROFILE_CHANGE_IMAGE, imageData);
  },

  updateActiveStatus: async (sellerId, statusData) => {
    return await apiService.patch(getEndpoint(API_ENDPOINTS.SELLERS.PROFILE_ACTIVE_STATUS, sellerId), statusData);
  },

  getBuyerList: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SELLERS.BUYER_LIST, { params });
  },

  getCatalogs: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SELLERS.CATALOGS, { params });
  },

  getCatalogsBySeller: async (sellerId, params = {}) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.SELLERS.CATALOGS_BY_SELLER, sellerId), { params });
  },

  uploadCatalog: async (catalogData) => {
    return await apiService.upload(API_ENDPOINTS.SELLERS.CATALOGS_UPLOAD, catalogData);
  },

  deleteCatalog: async (catalogId) => {
    return await apiService.delete(getEndpoint(API_ENDPOINTS.SELLERS.CATALOGS_DELETE, catalogId));
  },

  getCategoryList: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SELLERS.CATEGORY_LIST, { params });
  },
};

export const sanitarywareService = {
  getList: async (params = {}) => {
    return await apiService.post(API_ENDPOINTS.SANITARYWARE.LIST, params);
  },

  create: async (productData) => {
    return await apiService.post(API_ENDPOINTS.SANITARYWARE.CREATE, productData);
  },

  getDetail: async (id) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.SANITARYWARE.DETAIL, id));
  },

  update: async (id, productData) => {
    return await apiService.patch(getEndpoint(API_ENDPOINTS.SANITARYWARE.UPDATE, id), productData);
  },

  delete: async (id) => {
    return await apiService.delete(getEndpoint(API_ENDPOINTS.SANITARYWARE.DELETE, id));
  },

  updateActivationStatus: async (productId, statusData) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.SANITARYWARE.ACTIVATION_STATUS, productId), statusData);
  },

  updatePublishStatus: async (productId, statusData) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.SANITARYWARE.PUBLISH_STATUS, productId), statusData);
  },

  getImages: async (productId) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.SANITARYWARE.IMAGES_LIST, productId));
  },

  uploadImages: async (productId, imageData) => {
    return await apiService.upload(getEndpoint(API_ENDPOINTS.SANITARYWARE.IMAGES_UPLOAD, productId), imageData);
  },

  getImageDetail: async (productId, imageId) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.SANITARYWARE.IMAGES_DETAIL, productId, imageId));
  },

  deleteImage: async (productId, imageId) => {
    return await apiService.delete(getEndpoint(API_ENDPOINTS.SANITARYWARE.IMAGES_DELETE, productId, imageId));
  },

  changePrimaryImage: async (productId, imageId) => {
    return await apiService.patch(getEndpoint(API_ENDPOINTS.SANITARYWARE.IMAGES_CHANGE_PRIMARY, productId, imageId));
  },

  getAttributes: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_LIST, { params });
  },

  createAttribute: async (attributeData) => {
    return await apiService.post(API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_CREATE, attributeData);
  },

  getAttributeDetail: async (attrId) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_DETAIL, attrId));
  },

  updateAttribute: async (attrId, attributeData) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_UPDATE, attrId), attributeData);
  },

  deleteAttribute: async (attrId) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_DELETE, attrId));
  },

  previewUpdateAttribute: async (attrId, attributeData) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_PREVIEW_UPDATE, attrId), attributeData);
  },

  undoDeprecationAttribute: async (attrId) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_UNDO_DEPRECATION, attrId));
  },

  getTypes: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SANITARYWARE.TYPES_LIST, { params });
  },

  createType: async (typeData) => {
    return await apiService.post(API_ENDPOINTS.SANITARYWARE.TYPES_CREATE, typeData);
  },

  updateType: async (typeId, typeData) => {
    return await apiService.put(getEndpoint(API_ENDPOINTS.SANITARYWARE.TYPES_UPDATE, typeId), typeData);
  },

  deleteType: async (typeId, deleteData = {}) => {
    return await apiService.delete(getEndpoint(API_ENDPOINTS.SANITARYWARE.TYPES_DELETE, typeId), { data: deleteData });
  },

  undoDeprecationType: async (typeId) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.SANITARYWARE.TYPES_UNDO_DEPRECATION, typeId));
  },

  assignAttribute: async (typeId, attributeData) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.SANITARYWARE.TYPES_ASSIGN_ATTRIBUTE, typeId), attributeData);
  },

  assignMultipleAttributes: async (typeId, attributeIds) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.SANITARYWARE.TYPES_ASSIGN_MULTIPLE_ATTRIBUTES, typeId), {
      attribute_ids: attributeIds
    });
  },

  removeAttribute: async (typeId, attributeData) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.SANITARYWARE.TYPES_REMOVE_ATTRIBUTE, typeId), attributeData);
  },

  getTypeFilters: async (typeId) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.SANITARYWARE.TYPES_FILTERS, typeId));
  },
};

export const tileService = {
  getList: async (params = {}) => {
    return await apiService.post(API_ENDPOINTS.TILES.LIST, params);
  },

  create: async (productData) => {
    return await apiService.post(API_ENDPOINTS.TILES.CREATE, productData);
  },

  getDetail: async (id) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.TILES.DETAIL, id));
  },

  update: async (id, productData) => {
    return await apiService.patch(getEndpoint(API_ENDPOINTS.TILES.UPDATE, id), productData);
  },

  delete: async (id) => {
    return await apiService.delete(getEndpoint(API_ENDPOINTS.TILES.DELETE, id));
  },

  updateActivationStatus: async (productId, statusData) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.TILES.ACTIVATION_STATUS, productId), statusData);
  },

  updatePublishStatus: async (productId, statusData) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.TILES.PUBLISH_STATUS, productId), statusData);
  },

  getPdfs: async (productId) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.TILES.PDFS_LIST, productId));
  },

  uploadPdf: async (productId, pdfData) => {
    return await apiService.upload(getEndpoint(API_ENDPOINTS.TILES.PDFS_UPLOAD, productId), pdfData);
  },

  deletePdf: async (productId, pdfId) => {
    return await apiService.delete(getEndpoint(API_ENDPOINTS.TILES.PDFS_DELETE, productId, pdfId));
  },

  getSizes: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.TILES.SIZES_LIST, { params });
  },

  createSize: async (sizeData) => {
    return await apiService.post(API_ENDPOINTS.TILES.SIZES_CREATE, sizeData);
  },

  updateSize: async (sizeId, sizeData) => {
    return await apiService.put(getEndpoint(API_ENDPOINTS.TILES.SIZES_UPDATE, sizeId), sizeData);
  },

  deleteSize: async (sizeId, deleteData = {}) => {
    return await apiService.delete(getEndpoint(API_ENDPOINTS.TILES.SIZES_DELETE, sizeId), { data: deleteData });
  },

  undoDeprecationSize: async (sizeId) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.TILES.SIZES_UNDO_DEPRECATION, sizeId));
  },

  getTypes: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.TILES.TYPES_LIST, { params });
  },

  createType: async (typeData) => {
    return await apiService.post(API_ENDPOINTS.TILES.TYPES_CREATE, typeData);
  },

  updateType: async (typeId, typeData) => {
    return await apiService.put(getEndpoint(API_ENDPOINTS.TILES.TYPES_UPDATE, typeId), typeData);
  },

  deleteType: async (typeId, deleteData = {}) => {
    console.log('Enhanced service - deleteType called with:', { typeId, deleteData });
    return await apiService.delete(getEndpoint(API_ENDPOINTS.TILES.TYPES_DELETE, typeId), { data: deleteData });
  },

  undoDeprecationType: async (typeId) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.TILES.TYPES_UNDO_DEPRECATION, typeId));
  },
};

export const socialService = {
  getPosts: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SOCIAL.POSTS_LIST, { params });
  },

  createPost: async (postData) => {
    return await apiService.post(API_ENDPOINTS.SOCIAL.POSTS_CREATE, postData);
  },

  getPostDetail: async (postId) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.SOCIAL.POSTS_DETAIL, postId));
  },

  updatePost: async (postId, postData) => {
    return await apiService.patch(getEndpoint(API_ENDPOINTS.SOCIAL.POSTS_UPDATE, postId), postData);
  },

  deletePost: async (postId) => {
    return await apiService.delete(getEndpoint(API_ENDPOINTS.SOCIAL.POSTS_DELETE, postId));
  },

  getReviews: async (sellerId, params = {}) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.SOCIAL.REVIEWS_LIST, sellerId), { params });
  },

  createReview: async (sellerId, reviewData) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.SOCIAL.REVIEWS_CREATE, sellerId), reviewData);
  },

  deleteReview: async (reviewId) => {
    return await apiService.delete(getEndpoint(API_ENDPOINTS.SOCIAL.REVIEWS_DELETE, reviewId));
  },

  followSeller: async (sellerId, followData) => {
    return await apiService.post(getEndpoint(API_ENDPOINTS.SOCIAL.FOLLOWERS_FOLLOW, sellerId), followData);
  },

  unfollowSeller: async (sellerId) => {
    return await apiService.delete(getEndpoint(API_ENDPOINTS.SOCIAL.FOLLOWERS_UNFOLLOW, sellerId));
  },
};

export const subscriptionService = {
  getList: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SUBSCRIPTIONS.LIST, { params });
  },

  getPlans: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SUBSCRIPTIONS.PLANS_LIST, { params });
  },

  getPlanDetail: async (planId) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.SUBSCRIPTIONS.PLANS_DETAIL, planId));
  },

  createPlan: async (planData) => {
    return await apiService.post(API_ENDPOINTS.SUBSCRIPTIONS.PLANS_CREATE, planData);
  },

  updatePlan: async (planId, planData) => {
    return await apiService.put(getEndpoint(API_ENDPOINTS.SUBSCRIPTIONS.PLANS_UPDATE, planId), planData);
  },

  deletePlan: async (planId) => {
    return await apiService.delete(getEndpoint(API_ENDPOINTS.SUBSCRIPTIONS.PLANS_DELETE, planId));
  },

  getBuyerSubscriptions: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SUBSCRIPTIONS.BUYERS_LIST, { params });
  },

  getBuyerSubscriptionDetail: async (buyerId) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.SUBSCRIPTIONS.BUYERS_DETAIL, buyerId));
  },

  getSellerSubscriptions: async (params = {}) => {
    return await apiService.get(API_ENDPOINTS.SUBSCRIPTIONS.SELLERS_LIST, { params });
  },

  getSellerSubscriptionDetail: async (sellerId) => {
    return await apiService.get(getEndpoint(API_ENDPOINTS.SUBSCRIPTIONS.SELLERS_DETAIL, sellerId));
  },

  initiatePayment: async (paymentData) => {
    return await apiService.post(API_ENDPOINTS.SUBSCRIPTIONS.PAYMENT_INITIATE, paymentData);
  },

  verifyPayment: async (verificationData) => {
    return await apiService.post(API_ENDPOINTS.SUBSCRIPTIONS.PAYMENT_VERIFY, verificationData);
  },
};

export const enhancedServices = {
  account: accountService,
  buyer: buyerService,
  seller: sellerService,
  sanitaryware: sanitarywareService,
  tile: tileService,
  social: socialService,
  subscription: subscriptionService,
};

export default enhancedServices; 